/**
 * SidePanel Component
 *
 * Shared shell for sidebar panels (tasks, notes).
 * Handles positioning, slide animation, and mobile backdrop.
 * Content is determined by children — this component only provides the container.
 */

import { memo, type ReactNode } from 'react';
import clsx from 'clsx';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import styles from './SidePanel.module.css';

interface SidePanelProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}

export const SidePanel = memo(function SidePanel({
  isOpen,
  onClose,
  children,
  footer,
}: SidePanelProps) {
  const { isMobile } = useDeviceLayout();

  return (
    <>
      {isMobile && isOpen && (
        <div className={styles.backdrop} onClick={onClose} />
      )}

      <div
        className={clsx(
          styles.panel,
          isOpen && styles.open,
          isMobile && styles.mobile
        )}
      >
        <div className={styles.content}>{children}</div>
        {footer}
      </div>
    </>
  );
});
